import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LinkSimpleIcon, StackIcon, CopyIcon, PulseIcon, ArrowRightIcon, StorefrontIcon, WarningIcon, CheckCircleIcon } from "@phosphor-icons/react";
import api, { resolveUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import CreateTagDialog from "@/components/CreateTagDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const EMPTY_IMG = "https://images.unsplash.com/photo-1628891439478-c613e85af7d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxibGFuayUyMHNtYXJ0JTIwY2FyZCUyMG1vY2t8ZW58MHx8fHwxNzg2MTg3NzQ2fDA&ixlib=rb-4.1.0&q=85";

function copy(text) {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
}

const STATUS_STYLE = {
  active: "border-primary/40 text-primary",
  printing: "border-accent/40 text-accent",
  paused: "border-white/30 text-white/60",
  disabled: "border-destructive/40 text-destructive",
  unallocated: "border-white/20 text-white/40",
};

function TagRow({ tag, onClick }) {
  const isDirect = tag.mode === "direct";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`tag-row-${tag.code}`}
      className="group grid grid-cols-12 items-center gap-4 border-b border-border px-6 py-5 hover:bg-secondary/30 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <div className="col-span-12 md:col-span-4 flex items-center gap-3">
        <div className={`h-10 w-10 flex items-center justify-center border ${isDirect ? "border-primary/40 text-primary" : "border-accent/40 text-accent"}`}>
          {isDirect ? <LinkSimpleIcon weight="bold" /> : <StackIcon weight="bold" />}
        </div>
        <div className="min-w-0">
          <p className="font-heading font-bold truncate">{tag.label}</p>
          <span className="font-mono text-xs text-primary">/{tag.code}</span>
        </div>
      </div>

      <div className="col-span-8 md:col-span-4 min-w-0">
        <p className="font-mono text-xs text-white/45 truncate">
          {isDirect ? (tag.destination_url || "—") : "Hub · multiple links"}
        </p>
      </div>

      <div className="col-span-2 md:col-span-1">
        <Badge variant="outline" className={`font-mono text-[10px] uppercase ${STATUS_STYLE[tag.status] || "border-white/20 text-white/40"}`} data-testid={`tag-status-${tag.code}`}>
          {tag.status || "active"}
        </Badge>
      </div>

      <div className="col-span-1 md:col-span-2 flex items-center justify-end gap-4">
        <div className="text-right">
          <p className="font-mono text-lg leading-none">{tag.scan_count}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">scans</p>
        </div>
        <Button
          data-testid={`tag-copy-${tag.code}`}
          size="icon"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); copy(resolveUrl(tag.code)); }}
          className="text-white/40 hover:text-primary hover:bg-transparent"
        >
          <CopyIcon weight="bold" />
        </Button>
        <ArrowRightIcon weight="bold" className="text-white/20 group-hover:text-primary group-hover:translate-x-1 transition-all" />
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const [tags, setTags] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tags");
      setTags(data);
    } catch {
      setTags([]);
    }
  }, []);

  const [shop, setShop] = useState(null);
  const loadShop = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/shopify/status"); setShop(data); } catch { /* noop */ }
  }, [isAdmin]);

  const [portal, setPortal] = useState(null);
  const loadPortal = useCallback(async () => {
    try { const { data } = await api.get("/portal/orders"); setPortal(data); }
    catch { setPortal(null); }
  }, []);

  useEffect(() => { load(); loadShop(); loadPortal(); }, [load, loadShop, loadPortal]);

  const totalScans = (tags || []).reduce((a, t) => a + t.scan_count, 0);

  return (
    <Layout>
      {isAdmin && shop && (
        <div data-testid="shopify-status-banner" className="border border-border bg-card mb-8 grid sm:grid-cols-2">
          <div className="flex items-center gap-3 p-5 border-b sm:border-b-0 sm:border-r border-border">
            <div className={`h-10 w-10 flex items-center justify-center border ${shop.connected ? "border-primary/40 text-primary" : "border-accent/40 text-accent"}`}>
              <StorefrontIcon weight="bold" />
            </div>
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Shopify</p>
              {shop.connected ? (
                <p data-testid="shopify-connected" className="font-heading font-bold text-sm flex items-center gap-1.5 mt-0.5">
                  <CheckCircleIcon weight="fill" className="text-primary" /> Connected · {shop.store_domain}
                </p>
              ) : (
                <a href={shop.install_url} data-testid="shopify-install-link" className="font-heading font-bold text-sm text-accent hover:underline mt-0.5 inline-block">
                  Not connected — install on {shop.store_domain} →
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 p-5">
            <div className={`h-10 w-10 flex items-center justify-center border ${shop.pool_low ? "border-destructive/40 text-destructive" : "border-primary/40 text-primary"}`}>
              {shop.pool_low ? <WarningIcon weight="bold" /> : <PulseIcon weight="bold" />}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Unallocated code pool</p>
              <p data-testid="pool-count" className={`font-heading font-bold text-sm mt-0.5 ${shop.pool_low ? "text-destructive" : ""}`}>
                {shop.unallocated_pool} codes ready{shop.pool_low ? " · running low — generate a batch" : ""}
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-3">// Command center</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">Your codes</h1>
        </div>
        {isAdmin && <CreateTagDialog onCreated={load} />}
      </div>

      {portal && portal.orders.length > 0 && (
        <div data-testid="portal-orders" className="border border-border bg-card mb-10">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <PulseIcon weight="bold" className="text-primary" />
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Your D Code purchases</p>
          </div>
          {portal.orders.map((o) => (
            <div key={o.id} data-testid={`portal-order-${o.shopify_order_id}`} className="px-6 py-4 border-b border-border last:border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-heading font-bold truncate">{o.product_title || "D Code product"}</p>
                <p className="font-mono text-[11px] text-white/40">Order {o.shopify_order_id}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {(o.codes || []).map((c) => (
                  <Badge key={c} variant="outline" className="font-mono text-[10px] border-primary/40 text-primary">/{c}</Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 border border-border mb-10">
        <div className="p-6 border-r border-border">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Active codes</p>
          <p data-testid="stat-total-codes" className="font-mono text-3xl mt-2">{tags ? tags.length : "—"}</p>
        </div>
        <div className="p-6 md:border-r border-border">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Total scans</p>
          <p data-testid="stat-total-scans" className="font-mono text-3xl mt-2 text-primary">{tags ? totalScans : "—"}</p>
        </div>
        <div className="hidden md:flex p-6 items-center gap-2 text-white/30">
          <PulseIcon weight="bold" className="text-primary" />
          <span className="font-mono text-xs">live scan tracking</span>
        </div>
      </div>

      {tags === null ? (
        <div className="border border-border">
          {[0, 1, 2].map((i) => (
            <div key={i} className="px-6 py-5 border-b border-border"><Skeleton className="h-10 w-full bg-secondary/40" /></div>
          ))}
        </div>
      ) : tags.length === 0 ? (
        <div data-testid="empty-state" className="border border-border flex flex-col items-center text-center py-20 px-6">
          <img src={EMPTY_IMG} alt="" className="h-40 w-40 object-cover grayscale contrast-125 mb-8 border border-border" />
          <h3 className="font-heading font-black text-2xl tracking-tight">No tags generated yet</h3>
          {isAdmin ? (
            <>
              <p className="text-white/50 mt-3 max-w-sm">Create your first permanent code and point it anywhere. Change the destination whenever you like.</p>
              <div className="mt-8"><CreateTagDialog onCreated={load} /></div>
            </>
          ) : (
            <p className="text-white/50 mt-3 max-w-sm">Your codes appear here once you purchase a D Code product. Each one is yours to point anywhere, forever.</p>
          )}
        </div>
      ) : (
        <div className="border border-border">
          {tags.map((t) => (
            <TagRow key={t.id} tag={t} onClick={() => navigate(`/admin/tags/${t.id}`)} />
          ))}
        </div>
      )}
    </Layout>
  );
}
